import type { CampaignParticipantType } from '@/types/campaignParticipant.types'
import { ParticipantStatus } from '@/types/campaignParticipant.types'
import type { ParticipantsColumnType } from '@/types/campaignParticipants.types'

export const STATUS_TITLE_MAP: Record<ParticipantStatus, string> = {
  suggestion: 'Suggestions',
  second_prio: 'Second prio',
  open: 'Invited',
  applied: 'Applied',
  accepted: 'Approved',
  store_denied: 'Rejected by advertiser',
  denied: 'Rejected by creator',
  product_sent: 'Product sent',
  have_received: 'Product received',
  have_posted: 'Posted',
  done: 'Done',
}

export type ColumnSetType =
  | 'proposal'
  | 'proposalWithSeeding'
  | 'creatorApproval'
  | 'creatorApprovalWithSeeding'
  | 'advertiserApproval'
  | 'advertiserApprovalWithSeeding'
export const CAMPAIGN_COLUMN_SET_MAP: Record<ColumnSetType, ParticipantStatus[]> = {
  proposal: [
    ParticipantStatus.Suggestion,
    ParticipantStatus.SecondPrio,
    ParticipantStatus.Open,
    ParticipantStatus.StoreDenied,
    ParticipantStatus.Accepted,
    ParticipantStatus.Denied,
    ParticipantStatus.HavePosted,
    ParticipantStatus.Done,
  ],
  proposalWithSeeding: [
    ParticipantStatus.Suggestion,
    ParticipantStatus.SecondPrio,
    ParticipantStatus.Open,
    ParticipantStatus.StoreDenied,
    ParticipantStatus.Accepted,
    ParticipantStatus.Denied,
    ParticipantStatus.ProductSent,
    ParticipantStatus.HaveReceived,
    ParticipantStatus.HavePosted,
    ParticipantStatus.Done,
  ],
  creatorApproval: [
    ParticipantStatus.Open,
    ParticipantStatus.Accepted,
    ParticipantStatus.Denied,
    ParticipantStatus.HavePosted,
    ParticipantStatus.Done,
  ],
  creatorApprovalWithSeeding: [
    ParticipantStatus.Open,
    ParticipantStatus.Accepted,
    ParticipantStatus.Denied,
    ParticipantStatus.ProductSent,
    ParticipantStatus.HaveReceived,
    ParticipantStatus.HavePosted,
    ParticipantStatus.Done,
  ],
  advertiserApproval: [
    ParticipantStatus.Open,
    ParticipantStatus.Applied,
    ParticipantStatus.Denied,
    ParticipantStatus.Accepted,
    ParticipantStatus.StoreDenied,
    ParticipantStatus.HavePosted,
    ParticipantStatus.Done,
  ],
  advertiserApprovalWithSeeding: [
    ParticipantStatus.Open,
    ParticipantStatus.Applied,
    ParticipantStatus.Denied,
    ParticipantStatus.Accepted,
    ParticipantStatus.StoreDenied,
    ParticipantStatus.ProductSent,
    ParticipantStatus.HaveReceived,
    ParticipantStatus.HavePosted,
    ParticipantStatus.Done,
  ],
}

export const CAMPAIGN_COLUMN_LAYOUT: Record<ColumnSetType, ParticipantStatus[][]> = {
  proposal: [
    [ParticipantStatus.Suggestion, ParticipantStatus.SecondPrio],
    [ParticipantStatus.Open, ParticipantStatus.StoreDenied],
    [ParticipantStatus.Accepted, ParticipantStatus.Denied],
    [ParticipantStatus.HavePosted],
    [ParticipantStatus.Done],
  ],
  proposalWithSeeding: [
    [ParticipantStatus.Suggestion, ParticipantStatus.SecondPrio],
    [ParticipantStatus.Open, ParticipantStatus.StoreDenied],
    [ParticipantStatus.Accepted, ParticipantStatus.Denied],
    [ParticipantStatus.ProductSent],
    [ParticipantStatus.HaveReceived],
    [ParticipantStatus.HavePosted],
    [ParticipantStatus.Done],
  ],
  creatorApproval: [
    [ParticipantStatus.Open],
    [ParticipantStatus.Accepted, ParticipantStatus.Denied],
    [ParticipantStatus.HavePosted],
    [ParticipantStatus.Done],
  ],
  creatorApprovalWithSeeding: [
    [ParticipantStatus.Open],
    [ParticipantStatus.Accepted, ParticipantStatus.Denied],
    [ParticipantStatus.ProductSent],
    [ParticipantStatus.HaveReceived],
    [ParticipantStatus.HavePosted],
    [ParticipantStatus.Done],
  ],
  advertiserApproval: [
    [ParticipantStatus.Open],
    [ParticipantStatus.Applied, ParticipantStatus.Denied],
    [ParticipantStatus.Accepted, ParticipantStatus.StoreDenied],
    [ParticipantStatus.HavePosted],
    [ParticipantStatus.Done],
  ],
  advertiserApprovalWithSeeding: [
    [ParticipantStatus.Open],
    [ParticipantStatus.Applied, ParticipantStatus.Denied],
    [ParticipantStatus.Accepted, ParticipantStatus.StoreDenied],
    [ParticipantStatus.ProductSent],
    [ParticipantStatus.HaveReceived],
    [ParticipantStatus.HavePosted],
    [ParticipantStatus.Done],
  ],
}
export const COLLAPSIBLE_COLUMNS_LIST: ParticipantStatus[] = [
  ParticipantStatus.Denied,
  ParticipantStatus.StoreDenied,
]
export const COLUMN_COLORS: Record<ParticipantStatus, string> = {
  suggestion: '#DDDFE7',
  second_prio: '#DDDFE7',
  open: '#E6F7FE',
  applied: '#E8FBF5',
  accepted: '#B7E5D7',
  store_denied: '#FFEBEB',
  denied: '#FFEBEB',
  product_sent: '#FEF9E6',
  have_received: '#E8FBF5',
  have_posted: '#E6F7FE',
  done: '#DDDFE7',
}

export type DragCardActionType = {
  addedIndex: null | number
  payload: undefined | CampaignParticipantType
  removedIndex: null | number
}

export const isCardMovedWithinOneColumn = (
  removedIndex: null | number,
  addedIndex: null | number,
) => removedIndex !== null && addedIndex !== null

export const modifyColumnDataOnCardDrag = (
  items: CampaignParticipantType[],
  affectedItem: DragCardActionType,
  total: number,
): { items: CampaignParticipantType[]; total: number } => {
  const { removedIndex, addedIndex, payload } = affectedItem
  if (removedIndex === null && addedIndex === null) {
    return { items, total }
  }

  const result = [...items]
  let modifiedTotal = total

  // Case when we move item within one column
  if (isCardMovedWithinOneColumn(removedIndex, addedIndex)) {
    const [item] = result.splice(removedIndex || 0, 1)
    result.splice(addedIndex || 0, 0, item)
    return { items: result, total }
  }

  if (removedIndex !== null) {
    result.splice(removedIndex, 1)
    modifiedTotal = total - 1
  }

  if (addedIndex !== null && payload) {
    result.splice(addedIndex, 0, payload)
    modifiedTotal = total + 1
  }

  return { items: result, total: modifiedTotal }
}

export const modifyColumnsDataOnManualChange = ({
  participant,
  columnsData,
  newStatus,
  oldStatus,
}: {
  participant: CampaignParticipantType
  columnsData: Record<ParticipantStatus, ParticipantsColumnType>
  newStatus: ParticipantStatus
  oldStatus: ParticipantStatus
}) => {
  const fromColumnCards = columnsData[oldStatus].items
  const toColumnCards = columnsData[newStatus].items
  const affectedCard = fromColumnCards.find((card) => card?.id === participant.id)
  if (affectedCard && affectedCard.status !== participant.status) {
    affectedCard.status = participant.status
  }

  const updatedFromColumnItems = modifyColumnDataOnCardDrag(
    fromColumnCards,
    {
      addedIndex: null,
      removedIndex: fromColumnCards.findIndex((card) => card.id === participant.id),
      payload: affectedCard,
    },
    columnsData[oldStatus].total,
  )
  const updatedToColumnItems = modifyColumnDataOnCardDrag(
    toColumnCards,
    {
      addedIndex: 0,
      removedIndex: null,
      payload: affectedCard,
    },
    columnsData[newStatus].total,
  )

  return { updatedFromColumnItems, updatedToColumnItems }
}
